import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Calendar, CreditCard, Check, Shield, Clock, Truck, Loader2 } from 'lucide-react';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PayDeposit() {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(true);

  const DEPOSIT_AMOUNT = 165;

  // Fetch unavailable dates on mount
  useEffect(() => {
    const fetchUnavailableDates = async () => {
      try {
        const response = await axios.get(`${API}/availability/unavailable-dates`);
        if (response.data.success) {
          // Convert string dates to Date objects for comparison
          const dates = response.data.unavailable_dates.map(dateStr => {
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
          });
          setBlockedDates(dates);
        }
      } catch (error) {
        console.error('Error fetching unavailable dates:', error);
        // Don't block booking if fetch fails
      } finally {
        setIsLoadingDates(false);
      }
    };

    fetchUnavailableDates();
  }, []);

  const handlePayment = async () => {
    if (!selectedDate) {
      toast({
        title: "Date Required",
        description: "Please select your preferred moving date",
        variant: "destructive",
      });
      return;
    }

    if (!customerEmail || !customerName) {
      toast({
        title: "Contact Details Required",
        description: "Please enter your name and email address",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payment/create-checkout`, {
        package_type: 'standard_deposit',
        origin_url: originUrl,
        customer_email: customerEmail,
        customer_name: customerName,
        moving_date: selectedDate.toISOString()
      });

      // Store details in localStorage for later retrieval
      localStorage.setItem('booking_date', selectedDate.toISOString());
      localStorage.setItem('customer_email', customerEmail);
      localStorage.setItem('customer_name', customerName);

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Error",
        description: "Failed to process payment. Please try again.",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  // Disable past dates AND blocked dates
  const disabledDates = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable past dates
    if (date < today) return true;
    
    // Disable blocked dates
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.some(blocked => {
      const blockedStr = blocked.toISOString().split('T')[0];
      return dateStr === blockedStr;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Book Your Move
              </h1>
              <p className="text-xl text-gray-600">
                Secure your moving date with a $165 deposit
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Form */}
              <div className="space-y-6">
                {/* Pricing Info */}
                <Card className="border-2 border-yellow-400 bg-yellow-50">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-lg text-gray-900 mb-4">Our Rates</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">2 Movers + Truck</span>
                        <span className="font-bold text-xl text-gray-900">From $130/hour</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <span>GST included • Charged in 30-min increments</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <span>Minimum booking</span>
                        <span>2 hours</span>
                      </div>
                      <div className="flex justify-between items-center pt-3 border-t">
                        <span className="text-gray-700">Call-out fee</span>
                        <span className="font-semibold">Based on location</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-4">
                      Final cost depends on access, distance, and job complexity.
                    </p>
                  </CardContent>
                </Card>

                {/* Contact Information */}
                <Card className="border-2 border-gray-200">
                  <CardHeader>
                    <CardTitle>Your Contact Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <input
                        id="name"
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full mt-2 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                        placeholder="John Smith"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <input
                        id="email"
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        className="w-full mt-2 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                        placeholder="john@example.com"
                        required
                      />
                      <p className="text-sm text-gray-600 mt-1">We'll send your confirmation to this email</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Date Selection */}
                <Card className="border-2 border-gray-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-6 h-6 text-yellow-600" />
                      Select Moving Date
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-center">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={disabledDates}
                        className="rounded-md border"
                      />
                    </div>
                    {selectedDate && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-gray-700">
                          <strong>Selected Date:</strong> {selectedDate.toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Payment Summary */}
              <div>
                <Card className="border-2 border-yellow-400 shadow-xl sticky top-24">
                  <CardHeader className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-6 h-6" />
                      Payment Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-700">Preferred Moving Date</Label>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedDate ? selectedDate.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Not selected'}
                        </p>
                      </div>

                      <div className="border-t pt-4">
                        <div className="flex justify-between text-xl font-bold text-gray-900">
                          <span>Deposit to Pay</span>
                          <span className="text-yellow-600">${DEPOSIT_AMOUNT}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                          Remaining balance due on moving day
                        </p>
                      </div>

                      <Button
                        onClick={handlePayment}
                        disabled={isProcessing || !selectedDate}
                        className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold text-lg py-6 mt-4"
                      >
                        {isProcessing ? 'Processing...' : `Pay $${DEPOSIT_AMOUNT} Deposit`}
                      </Button>

                      <p className="text-xs text-gray-600 text-center mt-4">
                        Secure payment powered by Stripe
                      </p>

                      <div className="bg-gray-50 p-4 rounded-lg mt-6">
                        <h4 className="font-semibold text-gray-900 mb-3">What happens next?</h4>
                        <ul className="text-sm text-gray-700 space-y-2">
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            Pay secure deposit via Stripe
                          </li>
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            Receive confirmation email
                          </li>
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            We'll contact you to confirm details
                          </li>
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            Pay remaining balance on moving day
                          </li>
                        </ul>
                      </div>

                      {/* Trust Elements */}
                      <div className="grid grid-cols-2 gap-3 mt-6">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Shield className="w-4 h-4 text-green-600" />
                          Fully Insured
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4 text-green-600" />
                          Fast & Reliable
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Truck className="w-4 h-4 text-green-600" />
                          Professional Team
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-600" />
                          No Hidden Fees
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
